import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      size: {
        default: "h-10 max-lg:h-11 px-4 py-2",
        sm: "h-9 max-lg:h-11 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10 max-lg:h-11 max-lg:w-11",
      },
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-muted",
        primaryOutline:
          "border border-primary bg-background text-primary hover:bg-primary hover:text-primary-foreground [&_svg]:text-primary hover:[&_svg]:text-primary-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-muted",
        // No max-lg tap-target floor here, unlike every other variant: link
        // is the one variant used INSIDE running text ("refer to the <guide>
        // in..."), where a 44px min-height inflates the line box mid-sentence
        // — WCAG 2.5.8 exempts inline links for exactly this reason, and the
        // e2e tap-target gate allowlists them on the same grounds. max-lg:
        // h-auto cancels the size axis's max-lg:h-11 (twMerge only collapses
        // same-modifier utilities, so the base h-auto alone can't). A
        // STANDALONE link-styled action (its own row, not inside prose) is
        // not exempt — those call sites pass max-lg:min-h-11 themselves.
        link: "p-0 h-auto max-lg:h-auto text-primary underline-offset-4 hover:underline",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  tooltip?: string
}

/**
 * Primary action trigger with `variant` (default/destructive/outline/primaryOutline/secondary/ghost/link) and `size` axes. Composes as a child element via `asChild` (Radix Slot); optional `tooltip` prop wraps the button in a Tooltip.
 *
 * @category Actions
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, tooltip, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    const buttonElement = (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )

    if (!tooltip) return buttonElement

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{buttonElement}</TooltipTrigger>
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
